import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { hasPhpBackend, apiListReports, apiDeleteReport, apiSaveReport } from '@/lib/phpApi';
import { useAuth } from '@/hooks/useAuth';
import { ALL_STANDARDS } from '@/types/standards';
import ProfessionalReport from '@/components/ProfessionalReport';
import {
  generateSampleTrendReportPDF,
  generateDAFReportPDF,
  generateInfluentDafEffluentReportPDF,
  buildRegulatoryReportNumber,
  type DAFReportParams,
  type DAFReportResult,
  type DAFReportOptions,
  type ChainReportOptions,
  type ChainInfluentParams,
} from '@/lib/pdfGenerator';
import {
  FileText, TrendingUp, Trash2, Download, Eye, Search,
  Building2, ClipboardList, X, SortAsc, SortDesc, ChevronDown,
  Database, HardDrive, Calendar, Layers, Droplets, Activity,
  CheckCircle2, AlertCircle, Info, MapPin, Shield, Hash,
  ExternalLink, FlaskConical, Upload, RefreshCw, Beaker, BarChart3,
  ChevronRight,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { getAllLocalSamples } from '@/lib/hybridStorage';
import { toast } from 'sonner';

/* ─── PDF Preview Modal ────────────────────────────────────────────────────── */
function PDFPreviewModal({ url, title, onClose, onDownload }: {
  url: string; title: string; onClose: () => void; onDownload: () => void;
}) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-rb-surface rounded-2xl w-full max-w-5xl flex flex-col shadow-2xl overflow-hidden"
          style={{ height: '92vh' }}
        >
          {/* Modal toolbar */}
          <div className="flex items-center justify-between px-4 py-3 bg-rb-surface border-b border-rb-brand/20 shrink-0">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-rb-brand" />
              <span className="text-sm font-bold text-white truncate max-w-[400px]">{title}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-rb-brand/15 text-rb-brand border border-rb-brand/25 font-bold uppercase tracking-wider">
                Regulatory PDF
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rb-brand text-slate-200 hover:bg-yellow-400 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {/* PDF iframe */}
          <iframe
            src={url}
            className="flex-1 w-full bg-rb-surface"
            title="PDF Preview"
            style={{ border: 'none' }}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─── Status badge ──────────────────────────────────────────────────────────── */
function DAFStatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; icon: React.ElementType }> = {
    optimal:    { cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { cls: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', icon: Info },
    undersized: { cls: 'bg-rose-500/10 text-rose-400 border-rose-500/20', icon: AlertCircle },
    overloaded: { cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertCircle },
    oversized:  { cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Info },
  };
  const cfg = map[status] ?? map.acceptable;
  const Icon = cfg.icon;
  return (
    <span className={`flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${cfg.cls}`}>
      <Icon className="w-2.5 h-2.5" />{status}
    </span>
  );
}

/* ─── Standard badge colour ─────────────────────────────────────────────────── */
function stdBadgeClass(std: string) {
  if (std === 'UK_EA')    return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  if (std === 'AU_EPA')   return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (std === 'EU_UWWTD') return 'bg-violet-500/10 text-violet-400 border-violet-500/20';
  if (std === 'US_EPA')   return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return 'bg-slate-700 text-slate-400 border-slate-600';
}

/* ─── Report number normalisation ──────────────────────────────────────────────
 * Old formats stored in DB or localStorage:
 *   RB-38, RB-37           (bare numeric — no structure)
 *   RB-AU-2026-WQR-4973    (old buildWQRNumber format — dash-separated, no slash)
 *   RB-IN-2026-0001        (old genReportNumber format)
 *   RB-DAF-EA-2026-1234    (old generateDAFReportNumber format)
 * Correct format always contains a forward-slash:
 *   RB-AU/2026/SMP-12345   RB-UK/2026/INF-12345  etc.
 * Detection: any stored number that lacks "/" is treated as legacy and replaced.
 * ────────────────────────────────────────────────────────────────────────────── */
type ReportType = 'SMP' | 'INF' | 'EFF' | 'DAF';

function normalizeReportNum(
  rn: string | null | undefined,
  standard: string,
  type: ReportType = 'SMP',
): string {
  if (rn && rn.includes('/')) return rn;          // already correct regulatory format
  return buildRegulatoryReportNumber(type, standard);  // legacy or missing — regenerate
}

/* ─── Date formatter (handles null / invalid / MySQL DATETIME) ──────────────── */
function parseDate(d: string | null | undefined): Date | null {
  if (!d) return null;
  try {
    // MySQL DATETIME: "2026-05-10 14:23:00" — replace space with T for ISO 8601
    const iso = String(d).replace(' ', 'T');
    const dt = new Date(iso);
    return isNaN(dt.getTime()) ? null : dt;
  } catch { return null; }
}

function fmtDate(d: string | null | undefined) {
  const dt = parseDate(d);
  if (!dt) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateFull(d: string | null | undefined) {
  const dt = parseDate(d);
  if (!dt) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

/* ─── Param table helper ─────────────────────────────────────────────────────── */
const PARAM_LABELS: Record<string, string> = {
  ph: 'pH', bod: 'BOD', cod: 'COD', tss: 'TSS', nh3: 'NH₃-N',
  og: 'O&G', oandg: 'O&G', temperature: 'Temp', tds: 'TDS',
  conductivity: 'Cond.', lead: 'Pb', chromium: 'Cr',
  flowRate: 'Flow', turbidity: 'Turbidity', hardness: 'Hardness',
};
const PARAM_UNITS: Record<string, string> = {
  ph: '', bod: 'mg/L', cod: 'mg/L', tss: 'mg/L', nh3: 'mg/L',
  og: 'mg/L', oandg: 'mg/L', temperature: '°C', tds: 'mg/L',
  conductivity: 'µS/cm', lead: 'mg/L', chromium: 'mg/L',
  flowRate: 'm³/d', turbidity: 'NTU', hardness: 'mg/L',
};

/* ─── Compliance colour helper ───────────────────────────────────────────────── */
function paramColour(val: number | undefined, limit: number | undefined): string {
  if (val === undefined || limit === undefined || limit === 0) return 'text-slate-400';
  const pct = val / limit;
  if (pct > 1)    return 'text-red-400';
  if (pct > 0.8)  return 'text-amber-400';
  return 'text-emerald-400';
}
function paramBg(val: number | undefined, limit: number | undefined): string {
  if (val === undefined || limit === undefined || limit === 0) return 'bg-slate-800/60 border-slate-800/40';
  const pct = val / limit;
  if (pct > 1)   return 'bg-red-500/10 border-red-500/20';
  if (pct > 0.8) return 'bg-amber-500/10 border-amber-500/20';
  return 'bg-emerald-500/10 border-emerald-500/20';
}

/* ─── Inline SVG Sparkline ───────────────────────────────────────────────────── */
function Sparkline({ values, limit }: { values: (number | undefined)[]; limit?: number }) {
  const nums = values.map(v => (v !== undefined ? Number(v) : NaN)).filter(v => !isNaN(v));
  if (nums.length < 2) return <span className="text-[10px] text-slate-600">—</span>;
  const W = 64, H = 24, pad = 2;
  const minV = Math.min(...nums);
  const maxV = Math.max(...nums);
  const range = maxV - minV || 1;
  const pts = nums.map((v, i) => {
    const x = pad + (i / (nums.length - 1)) * (W - pad * 2);
    const y = (H - pad) - ((v - minV) / range) * (H - pad * 2);
    return `${x},${y}`;
  });
  const lastVal = nums[nums.length - 1];
  const colour = limit && lastVal !== undefined
    ? (lastVal / limit > 1 ? '#f87171' : lastVal / limit > 0.8 ? '#fbbf24' : '#34d399')
    : 'var(--rb-text-2)';
  return (
    <svg width={W} height={H} className="inline-block align-middle">
      <polyline points={pts.join(' ')} fill="none" stroke={colour} strokeWidth="1.5" strokeLinejoin="round" />
      {limit !== undefined && (
        <line
          x1={pad} y1={(H - pad) - ((limit - minV) / range) * (H - pad * 2)}
          x2={W - pad} y2={(H - pad) - ((limit - minV) / range) * (H - pad * 2)}
          stroke="#ef4444" strokeWidth="0.75" strokeDasharray="2,2" opacity="0.5"
        />
      )}
    </svg>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function ReportsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [activeTab, setActiveTab]           = useState<'sample' | 'daf' | 'chains'>('sample');
  const [viewingReport, setViewingReport]   = useState<any>(null);
  const [serverReports, setServerReports]   = useState<any[]>([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [previewUrl, setPreviewUrl]         = useState<string | null>(null);
  const [previewTitle, setPreviewTitle]     = useState('');
  const [previewDownloader, setPreviewDownloader] = useState<() => void>(() => () => {});
  const [search, setSearch]                 = useState('');
  const [filterSource, setFilterSource]     = useState<'all' | 'server' | 'local'>('all');
  const [sortOrder, setSortOrder]           = useState<'newest' | 'oldest'>('newest');
  const [deleteConfirm, setDeleteConfirm]   = useState<string | null>(null);
  const [expandedId, setExpandedId]         = useState<string | null>(null);
  const [syncingId, setSyncingId]           = useState<string | null>(null);
  const [syncingAll, setSyncingAll]         = useState(false);

  // Actual measured effluent samples (for chain delta analysis)
  const [actualEffluents, setActualEffluents] = useState<any[]>([]);
  useEffect(() => {
    try {
      const all = getAllLocalSamples();
      setActualEffluents(all.filter((s: any) => s.sampleType === 'effluent' || s.sample_type === 'effluent'));
    } catch { setActualEffluents([]); }
  }, [serverReports]);

  const loadReports = async () => {
    if (!hasPhpBackend()) return;
    setIsLoading(true);
    try {
      const rows = await apiListReports();
      setServerReports(rows || []);
    } catch (err: any) {
      console.warn('[ReportsPage] apiListReports failed:', err?.message);
      setServerReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, []);

  const localReports = useMemo(() => {
    try {
      const raw = localStorage.getItem('rb_sample_reports');
      const all: any[] = raw ? JSON.parse(raw) : [];
      const currentUserId = user ? Number(user.id) : 0;
      return currentUserId
        ? all.filter(r => !r.userId || Number(r.userId) === currentUserId)
        : all;
    } catch { return []; }
  }, [user, serverReports]);

  const normaliseSamples = (raw: any[]) =>
    raw.map((s: any) => ({
      name: s.name || s.sampleName || 'Unknown',
      referenceId: s.referenceId || s.reportNumber || undefined,
      date: s.date || new Date().toISOString().split('T')[0],
      params: typeof s.params === 'string' ? JSON.parse(s.params) : (s.params || {}),
    }));

  /* ── All reports ── */
  const allReports = useMemo(() => [
    ...serverReports.map((r: any) => {
      // PHP returns snake_case; new list.php returns camelCase aliases.
      // Support both so old cached responses still work.
      const rn    = r.reportNumber    ?? r.report_number;
      const title = r.reportTitle     ?? r.report_title     ?? '';
      const cAt   = r.createdAt       ?? r.created_at       ?? null;
      const cName = r.customerName    ?? r.customer_name    ?? undefined;
      const sName = r.siteName        ?? r.site_name        ?? undefined;
      const sAddr = r.siteAddress     ?? r.site_address     ?? undefined;
      const sPRef = r.sitePermitRef   ?? r.site_permit_ref  ?? r.permitRef ?? undefined;
      const sJur  = r.siteJurisdiction?? r.site_jurisdiction?? r.regulatoryAuthority ?? undefined;
      const rType = r.reportType      ?? r.report_type      ?? 'sample';
      const prepBy= r.preparedBy      ?? r.prepared_by      ?? undefined;

      let reportData: any = null;
      const rdRaw = r.reportData ?? r.report_data;
      if (rdRaw) {
        try { reportData = typeof rdRaw === 'string' ? JSON.parse(rdRaw) : rdRaw; } catch {}
      }

      const srvStd  = r.standard || 'UK_EA';
      const srvType: ReportType = rType === 'daf' ? 'DAF' : 'SMP';

      // Build a readable title: customer — site or fallback
      const displayTitle = (title && title !== 'Untitled' && title !== 'Report')
        ? title
        : [cName, sName].filter(Boolean).join(' — ') || 'Sample Trend Report';

      return {
        id: `srv_${r.id}`,
        reportType: rType,
        reportNumber: normalizeReportNum(rn, srvStd, srvType),
        reportTitle: displayTitle,
        customerName: cName,
        siteName: sName,
        siteAddress: sAddr,
        sitePermitRef: sPRef,
        siteJurisdiction: sJur,
        standard: srvStd,
        createdAt: cAt,
        source: 'server' as const,
        samples: Array.isArray(r.samples) ? normaliseSamples(r.samples) : [],
        reportData,
        preparedBy: prepBy,
      };
    }),
    ...localReports.map((r: any) => {
      const rn    = r.reportNumber ?? r.report_number;
      const title = r.reportTitle  ?? r.report_title  ?? '';
      const cAt   = r.createdAt    ?? r.created_at    ?? null;
      const cName = r.customerName ?? r.customer_name ?? undefined;
      const sName = r.siteName     ?? r.site_name     ?? undefined;

      let reportData: any = null;
      const rdRaw = r.reportData ?? r.report_data;
      if (rdRaw) {
        try { reportData = typeof rdRaw === 'string' ? JSON.parse(rdRaw) : rdRaw; } catch {}
      }
      const locStd  = r.standard || 'UK_EA';
      const locType: ReportType = (r.reportType ?? r.report_type) === 'daf' ? 'DAF' : 'SMP';

      const displayTitle = (title && title !== 'Untitled' && title !== 'Report')
        ? title
        : [cName, sName].filter(Boolean).join(' — ') || 'Sample Trend Report';

      return {
        id: `loc_${r.id}`,
        reportType: r.reportType ?? r.report_type ?? 'sample',
        reportNumber: normalizeReportNum(rn, locStd, locType),
        reportTitle: displayTitle,
        customerName: cName,
        siteName: sName,
        siteAddress: r.siteAddress ?? r.site_address ?? undefined,
        sitePermitRef: r.sitePermitRef ?? r.site_permit_ref ?? r.permitRef ?? undefined,
        siteJurisdiction: r.siteJurisdiction ?? r.site_jurisdiction ?? undefined,
        standard: locStd,
        createdAt: cAt,
        source: 'local' as const,
        samples: Array.isArray(r.samples) ? normaliseSamples(r.samples) : [],
        reportData,
        preparedBy: r.preparedBy ?? r.prepared_by ?? undefined,
      };
    }),
  ], [serverReports, localReports]);

  // Deduplicate: server version wins; local only shown if no server record with same report number exists
  const allReportsDeduped = useMemo(() => {
    const serverNums = new Set(allReports.filter(r => r.source === 'server').map(r => r.reportNumber));
    return allReports.filter(r => r.source === 'server' || !serverNums.has(r.reportNumber));
  }, [allReports]);

  type ReportItem = typeof allReports[0];

  const sampleReports = useMemo(() => allReportsDeduped.filter(r => r.reportType !== 'daf'), [allReportsDeduped]);
  const dafReports    = useMemo(() => allReportsDeduped.filter(r => r.reportType === 'daf'),  [allReportsDeduped]);

  const applyFilters = (list: ReportItem[]) => {
    if (filterSource !== 'all') list = list.filter(r => r.source === filterSource);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.reportNumber.toLowerCase().includes(q) ||
        r.reportTitle.toLowerCase().includes(q) ||
        (r.customerName || '').toLowerCase().includes(q) ||
        (r.siteName || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const ta = parseDate(a.createdAt)?.getTime() ?? 0;
      const tb = parseDate(b.createdAt)?.getTime() ?? 0;
      return sortOrder === 'newest' ? tb - ta : ta - tb;
    });
  };

  const filteredSampleReports = useMemo(() => applyFilters(sampleReports), [sampleReports, filterSource, search, sortOrder]);
  const filteredDAFReports    = useMemo(() => applyFilters(dafReports),    [dafReports, filterSource, search, sortOrder]);

  // Group reports into treatment chains by customerName
  const linkedChains = useMemo(() => {
    type Chain = {
      customerName: string;
      standard: string;
      sampleReport?: typeof allReportsDeduped[0];
      dafReport?: typeof allReportsDeduped[0];
      chainDate?: string | null;
      actualEffluentSample?: any;
    };
    const map = new Map<string, Chain>();
    for (const r of allReportsDeduped) {
      const key = (r.customerName || '(no customer)').toLowerCase();
      if (!map.has(key)) map.set(key, { customerName: r.customerName || '(no customer)', standard: r.standard });
      const entry = map.get(key)!;
      if (r.reportType !== 'daf' && !entry.sampleReport) {
        entry.sampleReport = r;
        if (!entry.chainDate) entry.chainDate = r.createdAt;
      }
      if (r.reportType === 'daf' && !entry.dafReport) {
        entry.dafReport = r;
        if (!entry.chainDate) entry.chainDate = r.createdAt;
      }
    }
    // Attach actual effluent sample if one exists for the customer
    const chains = Array.from(map.values()).filter(c => c.dafReport);
    for (const chain of chains) {
      const custNameLower = chain.customerName.toLowerCase();
      chain.actualEffluentSample = actualEffluents.find((s: any) => {
        const sName = (s.customerName || s.customer_name || '').toLowerCase();
        return sName === custNameLower && !(s.notes || '').includes('Auto-saved from DAF Calculator');
      }) || null;
    }
    return chains.sort((a, b) => {
      const ta = parseDate(a.chainDate)?.getTime() ?? 0;
      const tb = parseDate(b.chainDate)?.getTime() ?? 0;
      return tb - ta;
    });
  }, [allReportsDeduped, actualEffluents]);

  /* ── Sample PDF helpers ── */
  const buildPDFOptions = (r: ReportItem) => ({
    samples: r.samples.map(s => ({
      name: s.name,
      referenceId: (s as any).referenceId || (s as any).reportNumber || undefined,
      date: new Date(s.date),
      params: s.params,
    })),
    opts: {
      reportNumber: r.reportNumber,
      reportTitle: r.reportTitle,
      customerName: r.customerName,
      siteName: r.siteName,
      siteAddress: r.siteAddress,
      permitRef: r.sitePermitRef,
      regulatoryAuthority: r.siteJurisdiction,
      standard: r.standard,
      createdAt: parseDate(r.createdAt) ?? new Date(),
    },
  });

  const handlePreview = (r: ReportItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const { samples, opts } = buildPDFOptions(r);
      const url = generateSampleTrendReportPDF(samples, opts, 'preview');
      if (!url) { toast.error('Preview generation failed'); return; }
      setPreviewUrl(url);
      setPreviewTitle(`${r.reportNumber} — ${r.reportTitle}`);
      setPreviewDownloader(() => () => {
        generateSampleTrendReportPDF(samples, opts, 'save');
        toast.success(`Downloaded: ${r.reportNumber}`);
      });
    } catch (err: any) { toast.error(`Preview failed: ${err.message}`); }
  };

  const handleDirectPDF = (r: ReportItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      const { samples, opts } = buildPDFOptions(r);
      generateSampleTrendReportPDF(samples, opts, 'save');
      toast.success(`PDF downloaded: ${r.reportNumber}`);
    } catch (err: any) { toast.error(`PDF failed: ${err.message}`); }
  };

  /* ── DAF PDF helpers ── */
  const buildDAFPDFArgs = (r: ReportItem) => {
    const d = r.reportData;
    if (!d || !d.params) return null;
    const p: DAFReportParams = d.params;
    const result: DAFReportResult = {
      tssRemoval:  (d.tssRemoval ?? 0) / 100,
      ogRemoval:   (d.ogRemoval  ?? 0) / 100,
      bodRemoval:  (d.bodRemoval ?? 0) / 100,
      codRemoval:  (d.codRemoval ?? 0) / 100,
      effluentTSS: d.effluentTSS ?? 0,
      effluentOG:  d.effluentOG  ?? 0,
      effluentBOD: d.effluentBOD ?? 0,
      effluentCOD: d.effluentCOD ?? 0,
      effluentPH:  p.ph ?? 7,
      hlr:         d.hlr  ?? 0,
      airToSolids: d.airToSolids ?? 0,
      status:      d.status ?? 'acceptable',
    };
    const opts: DAFReportOptions = {
      reportNumber:        r.reportNumber,
      customerName:        r.customerName,
      siteName:            r.siteName,
      siteAddress:         r.siteAddress,
      permitRef:           r.sitePermitRef,
      regulatoryAuthority: r.siteJurisdiction,
      preparedBy:          r.preparedBy,
      standard:            r.standard,
      createdAt:           parseDate(r.createdAt) ?? new Date(),
    };
    return { params: p, result, opts };
  };

  const handleDAFPreview = (r: ReportItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const args = buildDAFPDFArgs(r);
    if (!args) { toast.error('Report data missing — cannot regenerate PDF'); return; }
    try {
      const url = generateDAFReportPDF(args.params, args.result, args.opts, 'preview');
      if (!url) { toast.error('Preview generation failed'); return; }
      setPreviewUrl(url);
      setPreviewTitle(`${r.reportNumber} — ${r.reportTitle}`);
      setPreviewDownloader(() => () => {
        generateDAFReportPDF(args.params, args.result, args.opts, 'save');
        toast.success(`Downloaded: ${r.reportNumber}`);
      });
    } catch (err: any) { toast.error(`Preview failed: ${err.message}`); }
  };

  const handleDAFPDF = (r: ReportItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const args = buildDAFPDFArgs(r);
    if (!args) { toast.error('Report data missing — cannot regenerate PDF'); return; }
    try {
      generateDAFReportPDF(args.params, args.result, args.opts, 'save');
      toast.success(`DAF report downloaded: ${r.reportNumber}`);
    } catch (err: any) { toast.error(`PDF failed: ${err.message}`); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleDeleteReport = async (r: ReportItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (r.source === 'server') {
      if (!hasPhpBackend()) return;
      try {
        await apiDeleteReport(parseInt(r.id.replace('srv_', '')));
        toast.info('Report deleted from server');
        loadReports();
      } catch (err: any) { toast.error(err.message || 'Delete failed'); }
    } else {
      const local = JSON.parse(localStorage.getItem('rb_sample_reports') || '[]')
        .filter((x: any) => `loc_${x.id}` !== r.id);
      localStorage.setItem('rb_sample_reports', JSON.stringify(local));
      toast.info('Local report removed');
      window.location.reload();
    }
    setDeleteConfirm(null);
  };

  /* ── Sync local report to DB ── */
  const handleSyncToDb = async (r: ReportItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!hasPhpBackend()) { toast.error('No database connection available'); return; }
    setSyncingId(r.id);
    try {
      const localId = r.id.replace('loc_', '');
      const raw: any[] = (() => { try { return JSON.parse(localStorage.getItem('rb_sample_reports') || '[]'); } catch { return []; } })();
      const entry = raw.find((x: any) => String(x.id) === localId) || {
        reportNumber:    r.reportNumber,
        reportTitle:     r.reportTitle,
        customerName:    r.customerName,
        siteName:        r.siteName,
        siteAddress:     r.siteAddress,
        sitePermitRef:   r.sitePermitRef,
        siteJurisdiction:r.siteJurisdiction,
        samples:         r.samples,
        standard:        r.standard,
        createdAt:       r.createdAt,
        reportType:      r.reportType,
        reportData:      r.reportData,
        preparedBy:      r.preparedBy,
      };
      await apiSaveReport(entry);
      // Remove from localStorage after successful DB save
      const updated = raw.filter((x: any) => String(x.id) !== localId);
      localStorage.setItem('rb_sample_reports', JSON.stringify(updated));
      toast.success(`${r.reportNumber} synced to database ✓`);
      await loadReports();
    } catch (err: any) {
      toast.error(`Sync failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setSyncingId(null);
    }
  };

  /* ── Sync ALL local reports to DB ── */
  const handleSyncAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasPhpBackend()) { toast.error('No database connection'); return; }
    if (localSyncable.length === 0) { toast.info('No local reports to sync'); return; }
    setSyncingAll(true);
    let ok = 0; let fail = 0;
    const raw: any[] = (() => { try { return JSON.parse(localStorage.getItem('rb_sample_reports') || '[]'); } catch { return []; } })();
    const idsToRemove: string[] = [];
    for (const r of localSyncable) {
      const localId = r.id.replace('loc_', '');
      const entry = raw.find((x: any) => String(x.id) === localId) || {
        reportNumber: r.reportNumber, reportTitle: r.reportTitle,
        customerName: r.customerName, siteName: r.siteName,
        siteAddress: r.siteAddress, sitePermitRef: r.sitePermitRef,
        siteJurisdiction: r.siteJurisdiction, samples: r.samples,
        standard: r.standard, createdAt: r.createdAt,
        reportType: r.reportType, reportData: r.reportData,
        preparedBy: r.preparedBy,
      };
      try {
        await apiSaveReport(entry);
        idsToRemove.push(localId);
        ok++;
      } catch { fail++; }
    }
    if (idsToRemove.length > 0) {
      const cleaned = raw.filter((x: any) => !idsToRemove.includes(String(x.id)));
      localStorage.setItem('rb_sample_reports', JSON.stringify(cleaned));
    }
    setSyncingAll(false);
    if (ok > 0) toast.success(`Synced ${ok} report${ok !== 1 ? 's' : ''} to database ✓`);
    if (fail > 0) toast.error(`${fail} report${fail !== 1 ? 's' : ''} failed to sync`);
    await loadReports();
  };

  /* ── Full-screen report viewer ── */
  if (viewingReport) {
    return (
      <ProfessionalReport
        reportNumber={viewingReport.reportNumber}
        reportTitle={viewingReport.reportTitle}
        customerName={viewingReport.customerName}
        siteName={viewingReport.siteName}
        siteAddress={viewingReport.siteAddress}
        sitePermitRef={viewingReport.sitePermitRef}
        siteJurisdiction={viewingReport.siteJurisdiction}
        samples={viewingReport.samples}
        createdAt={viewingReport.createdAt}
        standard={viewingReport.standard}
        onBack={() => setViewingReport(null)}
      />
    );
  }

  const serverCount   = allReportsDeduped.filter(r => r.source === 'server').length;
  const localCount    = allReportsDeduped.filter(r => r.source === 'local').length;
  const localSyncable = allReportsDeduped.filter(r => r.source === 'local');
  const toggleExpand = (id: string) => setExpandedId(prev => prev === id ? null : id);

  /* ═══════════════════════════════════════════════════════════════════════════
     SAMPLE REPORT CARD
  ═══════════════════════════════════════════════════════════════════════════ */
  const renderSampleCard = (r: ReportItem) => {
    const isExpanded = expandedId === r.id;
    // Collect unique param keys across all samples
    const paramKeys = Array.from(
      new Set(r.samples.flatMap(s => Object.keys(s.params || {})))
    ).slice(0, 8);

    return (
      <motion.div
        key={r.id}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative rounded-xl border transition-all duration-200 overflow-hidden
          ${isExpanded
            ? 'border-rb-brand/40 bg-rb-surface/70 shadow-lg shadow-[#cda42e]/5'
            : 'border-slate-800/40 bg-rb-surface/60 hover:border-slate-500/50 hover:bg-[#0d1e32]/80 cursor-pointer'
          }`}
      >
        {/* Delete confirm overlay */}
        <AnimatePresence>
          {deleteConfirm === r.id && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 bg-rb-elevated/96 rounded-xl flex items-center justify-center gap-3"
              onClick={e => e.stopPropagation()}
            >
              <span className="text-xs text-slate-300">Delete <strong className="text-white font-mono">{r.reportNumber}</strong>?</span>
              <button onClick={(e) => handleDeleteReport(r, e)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25">
                Delete
              </button>
              <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-800 text-slate-400 border border-slate-700">
                Cancel
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Collapsed header (always visible, click to expand) ── */}
        <div
          className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
          onClick={() => toggleExpand(r.id)}
        >
          {/* Icon */}
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors
            ${isExpanded ? 'bg-rb-brand/15 border border-rb-brand/30' : 'bg-rb-brand/10 border border-rb-brand/15'}`}>
            <FileText className="w-4 h-4 text-rb-brand" />
          </div>

          {/* Main info */}
          <div className="flex-1 min-w-0">
            {/* Top row */}
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-[11px] font-mono text-rb-brand font-bold tracking-tight">{r.reportNumber}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${
                r.source === 'server'
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
              }`}>{r.source === 'server' ? '✦ DB' : '⬡ Local'}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${stdBadgeClass(r.standard)}`}>
                {r.standard}
              </span>
              {r.siteName && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 border border-slate-800/50 flex items-center gap-0.5">
                  <MapPin className="w-2 h-2" />{r.siteName}
                </span>
              )}
            </div>
            {/* Title */}
            <p className="text-xs font-semibold text-white truncate leading-snug">{r.reportTitle}</p>
            {/* Meta row */}
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              {r.customerName && (
                <span className="text-[10px] text-slate-500 flex items-center gap-1">
                  <Building2 className="w-2.5 h-2.5" />{r.customerName}
                </span>
              )}
              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                <Layers className="w-2.5 h-2.5" />{r.samples?.length || 0} sample{r.samples?.length !== 1 ? 's' : ''}
              </span>
              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                <Calendar className="w-2.5 h-2.5" />{fmtDate(r.createdAt)}
              </span>
            </div>
          </div>

          {/* Quick actions (stop propagation to not toggle expand) */}
          <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
            {r.source === 'local' && hasPhpBackend() && (
              <button
                onClick={(e) => handleSyncToDb(r, e)}
                disabled={syncingId === r.id}
                title="Save to database"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all disabled:opacity-50"
              >
                {syncingId === r.id
                  ? <RefreshCw className="w-3 h-3 animate-spin" />
                  : <><Upload className="w-3 h-3" /> Sync</>}
              </button>
            )}
            <button
              onClick={(e) => handlePreview(r, e)}
              title="Preview regulatory PDF"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-slate-800/80 text-slate-300 border border-slate-800/60 hover:bg-slate-700 hover:text-white transition-all"
            >
              <Eye className="w-3 h-3" /> Preview
            </button>
            <button
              onClick={(e) => handleDirectPDF(r, e)}
              title="Download regulatory PDF"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-rb-active/80 text-white border border-rb-brand/25 hover:bg-rb-active transition-all"
            >
              <Download className="w-3 h-3 text-rb-brand" /> PDF
            </button>
          </div>

          {/* Expand chevron */}
          <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-rb-brand' : ''}`} />
        </div>

        {/* ── Expanded content ── */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-slate-800/50 px-4 pt-4 pb-5 space-y-4">

                {/* ── Report identity block ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Report Reference</p>
                    <p className="text-[11px] font-mono font-bold text-rb-brand break-all">{r.reportNumber}</p>
                  </div>
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Regulatory Standard</p>
                    <p className="text-[11px] font-bold text-white">{r.standard}</p>
                  </div>
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Date Generated</p>
                    <p className="text-[11px] font-medium text-white">{fmtDateFull(r.createdAt)}</p>
                  </div>
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Storage</p>
                    <p className={`text-[11px] font-bold ${r.source === 'server' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {r.source === 'server' ? '✦ Confirmed in DB' : '⬡ Local device only'}
                    </p>
                  </div>
                </div>

                {/* ── Site details ── */}
                {(r.customerName || r.siteName || r.siteAddress || r.sitePermitRef || r.siteJurisdiction) && (
                  <div className="bg-slate-800/30 border border-slate-800/40 rounded-lg p-3 space-y-1.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Site & Regulatory Details</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {r.customerName && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">{r.customerName}</span>
                        </div>
                      )}
                      {r.siteName && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">{r.siteName}</span>
                        </div>
                      )}
                      {r.siteAddress && (
                        <div className="flex items-center gap-2 sm:col-span-2">
                          <MapPin className="w-3 h-3 text-slate-600 shrink-0" />
                          <span className="text-[11px] text-slate-400">{r.siteAddress}</span>
                        </div>
                      )}
                      {r.sitePermitRef && (
                        <div className="flex items-center gap-2">
                          <Hash className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">Permit: <span className="font-mono text-rb-brand">{r.sitePermitRef}</span></span>
                        </div>
                      )}
                      {r.siteJurisdiction && (
                        <div className="flex items-center gap-2">
                          <Shield className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">{r.siteJurisdiction}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Sample data table ── */}
                {r.samples.length > 0 && (
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">
                      Sample Data — {r.samples.length} sample{r.samples.length !== 1 ? 's' : ''} included
                    </p>
                    <div className="overflow-x-auto rounded-lg border border-slate-800/40">
                      <table className="w-full text-[10px]">
                        <thead>
                          <tr className="bg-slate-800/60 border-b border-slate-800/40">
                            <th className="text-left px-2 py-2 text-slate-500 font-semibold w-8">#</th>
                            <th className="text-left px-3 py-2 text-slate-400 font-semibold">Sample</th>
                            <th className="text-left px-3 py-2 text-slate-400 font-semibold">Date</th>
                            {paramKeys.map(k => (
                              <th key={k} className="text-right px-2 py-2 text-slate-400 font-semibold whitespace-nowrap">
                                {PARAM_LABELS[k] || k}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const std = ALL_STANDARDS.find(s => s.code === r.standard);
                            const limMap: Record<string, number | undefined> = {
                              tss: std?.limits.tss, bod: std?.limits.bod5, cod: std?.limits.cod,
                              nh3: std?.limits.ammoniaN, og: std?.limits.oilGrease,
                              oandg: std?.limits.oilGrease, lead: std?.limits.lead,
                              chromium: std?.limits.chromium, tds: std?.limits.tds,
                              temperature: std?.limits.temperature,
                            };
                            return r.samples.map((s, i) => (
                              <tr key={i} className={`border-b border-slate-800/50 ${i % 2 === 0 ? 'bg-slate-800/20' : 'bg-transparent'}`}>
                                <td className="px-2 py-2 text-slate-600 font-mono text-[10px] text-center">{i + 1}</td>
                                <td className="px-3 py-2 text-slate-300 font-medium max-w-[180px] truncate">{s.name}</td>
                                <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtDate(s.date)}</td>
                                {paramKeys.map(k => {
                                  const raw = s.params[k];
                                  const num = raw !== undefined ? parseFloat(String(raw)) : undefined;
                                  const lim = limMap[k];
                                  return (
                                    <td key={k} className="px-2 py-1.5 text-right whitespace-nowrap">
                                      {num !== undefined
                                        ? <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded border font-semibold ${paramBg(num, lim)} ${paramColour(num, lim)}`}>
                                            {raw}<span className="text-slate-600 ml-0.5 font-normal">{PARAM_UNITS[k] || ''}</span>
                                          </span>
                                        : <span className="text-slate-400">—</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── Trend sparklines ── */}
                {r.samples.length >= 2 && paramKeys.length > 0 && (() => {
                  const std = ALL_STANDARDS.find(s => s.code === r.standard);
                  const limMap: Record<string, number | undefined> = {
                    tss: std?.limits.tss, bod: std?.limits.bod5, cod: std?.limits.cod,
                    nh3: std?.limits.ammoniaN, og: std?.limits.oilGrease,
                    oandg: std?.limits.oilGrease, lead: std?.limits.lead,
                    chromium: std?.limits.chromium, tds: std?.limits.tds,
                    temperature: std?.limits.temperature,
                  };
                  return (
                    <div>
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1">
                        <BarChart3 className="w-3 h-3" /> Parameter Trends ({r.samples.length} samples)
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {paramKeys.map(k => {
                          const vals = r.samples.map(s => s.params[k] !== undefined ? parseFloat(String(s.params[k])) : undefined);
                          return (
                            <div key={k} className="flex flex-col items-center gap-0.5 bg-slate-800/30 border border-slate-800/30 rounded-lg px-2.5 py-2 min-w-[80px]">
                              <span className="text-[9px] text-slate-500 font-semibold">{PARAM_LABELS[k] || k}</span>
                              <Sparkline values={vals} limit={limMap[k]} />
                              {limMap[k] !== undefined && (
                                <span className="text-[8px] text-slate-600">lim {limMap[k]} {PARAM_UNITS[k] || ''}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="flex items-center gap-1 text-[9px] text-emerald-400"><span className="w-3 h-0.5 bg-emerald-400 inline-block rounded" /> Compliant (&lt;80% limit)</span>
                        <span className="flex items-center gap-1 text-[9px] text-amber-400"><span className="w-3 h-0.5 bg-amber-400 inline-block rounded" /> Caution (80–100%)</span>
                        <span className="flex items-center gap-1 text-[9px] text-red-400"><span className="w-3 h-0.5 bg-red-400 inline-block rounded" /> Exceedance (&gt;100%)</span>
                        <span className="flex items-center gap-1 text-[9px] text-red-400/50"><span className="w-3 h-0 border-t border-dashed border-red-400/50 inline-block" style={{marginBottom:'1px'}} /> Limit</span>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Action bar ── */}
                <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-800/40">
                  <button
                    onClick={(e) => handlePreview(r, e)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-800 text-slate-200 border border-slate-800/60 hover:bg-slate-700 hover:text-white transition-all"
                  >
                    <Eye className="w-3.5 h-3.5 text-rb-brand" /> Preview Regulatory PDF
                  </button>
                  <button
                    onClick={(e) => handleDirectPDF(r, e)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-rb-active text-white border border-rb-brand/30 hover:bg-rb-surface hover:border-rb-brand/50 transition-all"
                  >
                    <Download className="w-3.5 h-3.5 text-rb-brand" /> Download PDF
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setViewingReport(r); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-teal-500/10 text-teal-400 border border-teal-500/20 hover:bg-teal-500/20 transition-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open Full Assessment
                  </button>
                  {r.source === 'local' && hasPhpBackend() && (
                    <button
                      onClick={(e) => handleSyncToDb(r, e)}
                      disabled={syncingId === r.id}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all disabled:opacity-50"
                    >
                      {syncingId === r.id
                        ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Syncing…</>
                        : <><Upload className="w-3.5 h-3.5" /> Save to DB</>}
                    </button>
                  )}
                  <div className="flex-1" />
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(r.id); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-transparent hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     DAF REPORT CARD
  ═══════════════════════════════════════════════════════════════════════════ */
  const renderDAFCard = (r: ReportItem) => {
    const d = r.reportData;
    const isExpanded = expandedId === r.id;
    const statusAccent =
      d?.status === 'optimal'    ? 'bg-emerald-500' :
      d?.status === 'acceptable' ? 'bg-cyan-500' :
      d?.status === 'oversized'  ? 'bg-amber-500' : 'bg-rose-500';

    return (
      <motion.div
        key={r.id}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative rounded-xl border transition-all duration-200 overflow-hidden
          ${isExpanded
            ? 'border-blue-500/40 bg-rb-surface/70 shadow-lg shadow-blue-500/5'
            : 'border-slate-800/40 bg-rb-surface/60 hover:border-slate-500/50 hover:bg-[#0d1e32]/80 cursor-pointer'
          }`}
      >
        {/* Status accent strip */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${statusAccent}`} />

        {/* Delete confirm overlay */}
        <AnimatePresence>
          {deleteConfirm === r.id && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 bg-rb-elevated/96 rounded-xl flex items-center justify-center gap-3"
              onClick={e => e.stopPropagation()}
            >
              <span className="text-xs text-slate-300">Delete <strong className="text-white font-mono">{r.reportNumber}</strong>?</span>
              <button onClick={(e) => handleDeleteReport(r, e)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25">
                Delete
              </button>
              <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-800 text-slate-400 border border-slate-700">
                Cancel
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Collapsed header ── */}
        <div
          className="flex items-center gap-3 px-4 py-3 pl-5 cursor-pointer select-none"
          onClick={() => toggleExpand(r.id)}
        >
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors
            ${isExpanded ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-500/10 border border-blue-500/15'}`}>
            <Droplets className="w-4 h-4 text-blue-400" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-[11px] font-mono text-rb-brand font-bold tracking-tight">{r.reportNumber}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${
                r.source === 'server'
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
              }`}>{r.source === 'server' ? '✦ DB' : '⬡ Local'}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 font-bold">DAF Effluent</span>
              {d?.status && <DAFStatusBadge status={d.status} />}
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${stdBadgeClass(r.standard)}`}>{r.standard}</span>
            </div>
            <p className="text-xs font-semibold text-white truncate leading-snug">{r.reportTitle}</p>
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              {r.customerName && (
                <span className="text-[10px] text-slate-500 flex items-center gap-1">
                  <Building2 className="w-2.5 h-2.5" />{r.customerName}
                </span>
              )}
              {d?.tssRemoval != null && (
                <span className="text-[10px] text-slate-500">
                  TSS <span className="text-emerald-400 font-bold">{d.tssRemoval}%↓</span>
                </span>
              )}
              {d?.ogRemoval != null && (
                <span className="text-[10px] text-slate-500">
                  O&G <span className="text-emerald-400 font-bold">{d.ogRemoval}%↓</span>
                </span>
              )}
              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                <Calendar className="w-2.5 h-2.5" />{fmtDate(r.createdAt)}
              </span>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
            <button
              onClick={(e) => handleDAFPreview(r, e)}
              title="Preview DAF regulatory PDF"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-slate-800/80 text-slate-300 border border-slate-800/60 hover:bg-slate-700 hover:text-white transition-all"
            >
              <Eye className="w-3 h-3" /> Preview
            </button>
            <button
              onClick={(e) => handleDAFPDF(r, e)}
              title="Download DAF regulatory PDF"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-rb-active/80 text-white border border-rb-brand/25 hover:bg-rb-active transition-all"
            >
              <Download className="w-3 h-3 text-rb-brand" /> PDF
            </button>
          </div>

          <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-blue-400' : ''}`} />
        </div>

        {/* ── Expanded content ── */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="border-t border-slate-800/50 px-4 pt-4 pb-5 space-y-4 pl-5">

                {/* Identity block */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Report Reference</p>
                    <p className="text-[11px] font-mono font-bold text-rb-brand break-all">{r.reportNumber}</p>
                  </div>
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Regulatory Standard</p>
                    <p className="text-[11px] font-bold text-white">{r.standard}</p>
                  </div>
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">Date Generated</p>
                    <p className="text-[11px] font-medium text-white">{fmtDateFull(r.createdAt)}</p>
                  </div>
                  <div className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1 font-semibold">DAF Status</p>
                    <p className="text-[11px] font-bold text-white capitalize">{d?.status || '—'}</p>
                  </div>
                </div>

                {/* Site details */}
                {(r.customerName || r.siteName || r.siteAddress || r.sitePermitRef || r.siteJurisdiction) && (
                  <div className="bg-slate-800/30 border border-slate-800/40 rounded-lg p-3">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Site & Regulatory Details</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {r.customerName && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">{r.customerName}</span>
                        </div>
                      )}
                      {r.siteName && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">{r.siteName}</span>
                        </div>
                      )}
                      {r.siteAddress && (
                        <div className="flex items-center gap-2 sm:col-span-2">
                          <MapPin className="w-3 h-3 text-slate-600 shrink-0" />
                          <span className="text-[11px] text-slate-400">{r.siteAddress}</span>
                        </div>
                      )}
                      {r.sitePermitRef && (
                        <div className="flex items-center gap-2">
                          <Hash className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">Permit: <span className="font-mono text-rb-brand">{r.sitePermitRef}</span></span>
                        </div>
                      )}
                      {r.siteJurisdiction && (
                        <div className="flex items-center gap-2">
                          <Shield className="w-3 h-3 text-slate-500 shrink-0" />
                          <span className="text-[11px] text-slate-300">{r.siteJurisdiction}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* DAF Performance metrics */}
                {d && (
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold mb-2">DAF Performance Results</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { label: 'TSS Removal', value: d.tssRemoval != null ? `${d.tssRemoval}%` : null, accent: 'text-emerald-400' },
                        { label: 'O&G Removal', value: d.ogRemoval  != null ? `${d.ogRemoval}%`  : null, accent: 'text-emerald-400' },
                        { label: 'BOD Removal', value: d.bodRemoval != null ? `${d.bodRemoval}%` : null, accent: 'text-emerald-400' },
                        { label: 'COD Removal', value: d.codRemoval != null ? `${d.codRemoval}%` : null, accent: 'text-emerald-400' },
                        { label: 'Eff. TSS',    value: d.effluentTSS != null ? `${d.effluentTSS} mg/L` : null, accent: 'text-sky-400' },
                        { label: 'Eff. O&G',    value: d.effluentOG  != null ? `${d.effluentOG} mg/L`  : null, accent: 'text-sky-400' },
                        { label: 'HLR',         value: d.hlr != null ? `${d.hlr} m/h`         : null, accent: 'text-blue-400' },
                        { label: 'Air:Solids',  value: d.airToSolids != null ? `${d.airToSolids}` : null, accent: 'text-blue-400' },
                      ].filter(m => m.value != null).map(m => (
                        <div key={m.label} className="bg-slate-800/40 border border-slate-800/40 rounded-lg p-2.5">
                          <p className="text-[9px] text-slate-500 mb-0.5">{m.label}</p>
                          <p className={`text-sm font-bold ${m.accent}`}>{m.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action bar */}
                <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-800/40">
                  <button
                    onClick={(e) => handleDAFPreview(r, e)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-800 text-slate-200 border border-slate-800/60 hover:bg-slate-700 hover:text-white transition-all"
                  >
                    <Eye className="w-3.5 h-3.5 text-rb-brand" /> Preview Regulatory PDF
                  </button>
                  <button
                    onClick={(e) => handleDAFPDF(r, e)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-rb-active text-white border border-rb-brand/30 hover:bg-rb-surface hover:border-rb-brand/50 transition-all"
                  >
                    <Download className="w-3.5 h-3.5 text-rb-brand" /> Download PDF
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate('/daf-spec'); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-all"
                  >
                    <FlaskConical className="w-3.5 h-3.5" /> Open DAF Calculator
                  </button>
                  <div className="flex-1" />
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(r.id); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-transparent hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     PAGE LAYOUT
  ═══════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* PDF Preview Modal */}
      {previewUrl && (
        <PDFPreviewModal
          url={previewUrl}
          title={previewTitle}
          onClose={closePreview}
          onDownload={previewDownloader}
        />
      )}

      <PageHeader
        icon={FileText}
        title="Regulatory Reports"
        subtitle="Assessment reports, compliance certificates and treatment chain PDFs"
        actions={
          <div className="flex items-center gap-2">
            {localSyncable.length > 0 && hasPhpBackend() && (
              <button
                onClick={handleSyncAll}
                disabled={syncingAll}
                title={`Sync ${localSyncable.length} local report${localSyncable.length !== 1 ? 's' : ''} to database`}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all disabled:opacity-50"
              >
                {syncingAll
                  ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Syncing…</>
                  : <><Upload className="w-3.5 h-3.5" /> Sync {localSyncable.length} to DB</>}
              </button>
            )}
            <button
              onClick={() => navigate('/samples')}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/25 hover:bg-rb-brand/20 transition-all"
            >
              <TrendingUp className="w-3.5 h-3.5" /> Generate Report
            </button>
          </div>
        }
      />

      {/* ── Professional Tab Bar ── */}
      {(() => {
        const sampleLocalCount = sampleReports.filter(r => r.source === 'local').length;
        const dafLocalCount    = dafReports.filter(r => r.source === 'local').length;

        const tabs = [
          {
            id: 'sample' as const,
            label: 'Sample Trend Reports',
            icon: FileText,
            count: sampleReports.length,
            hasLocal: sampleLocalCount > 0,
            localCount: sampleLocalCount,
            activeText: 'text-rb-brand',
            activeBorder: 'border-rb-brand',
            activeBadge: 'bg-rb-brand/15 text-rb-brand',
          },
          {
            id: 'daf' as const,
            label: 'DAF Effluent Reports',
            icon: Droplets,
            count: dafReports.length,
            hasLocal: dafLocalCount > 0,
            localCount: dafLocalCount,
            activeText: 'text-blue-400',
            activeBorder: 'border-blue-400',
            activeBadge: 'bg-blue-500/15 text-blue-400',
          },
          {
            id: 'chains' as const,
            label: 'Treatment Chains',
            icon: Activity,
            count: linkedChains.length,
            hasLocal: false,
            localCount: 0,
            activeText: 'text-violet-400',
            activeBorder: 'border-violet-400',
            activeBadge: 'bg-violet-500/15 text-violet-400',
          },
        ];

        return (
          <div className="border-b border-slate-800/40">
            <div className="flex items-end -mb-px">
              {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-2 px-5 py-3 text-xs font-semibold border-b-2 transition-all whitespace-nowrap ${
                      isActive
                        ? `${tab.activeBorder} ${tab.activeText} bg-slate-800/25`
                        : 'border-transparent text-slate-500 hover:text-slate-300 hover:bg-slate-800/15'
                    }`}
                  >
                    <tab.icon className={`w-3.5 h-3.5 ${isActive ? tab.activeText : 'text-slate-600'}`} />
                    {tab.label}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      isActive ? tab.activeBadge : 'bg-slate-700/60 text-slate-500'
                    }`}>{tab.count}</span>
                    {tab.hasLocal && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0"
                        title={`${tab.localCount} stored locally — not yet confirmed in database`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Summary Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rb-card px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center shrink-0">
            <FileText className="w-3.5 h-3.5 text-rb-brand" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Total Reports</p>
            <p className="text-lg font-bold text-white leading-tight">{allReportsDeduped.length}</p>
          </div>
        </div>
        <div className="rb-card px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
            <Database className="w-3.5 h-3.5 text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Confirmed in DB</p>
            <p className="text-lg font-bold text-white leading-tight">{serverCount}</p>
          </div>
        </div>
        <div className="rb-card px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
            <HardDrive className="w-3.5 h-3.5 text-amber-400" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Local Only</p>
            <p className="text-lg font-bold text-white leading-tight">{localCount}</p>
          </div>
        </div>
        <div className="rb-card px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center shrink-0">
            <Activity className="w-3.5 h-3.5 text-violet-400" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Treatment Chains</p>
            <p className="text-lg font-bold text-white leading-tight">{linkedChains.length}</p>
          </div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by report number, client, site…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-800/60 border border-slate-800/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-rb-brand/40 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-800/50 rounded-xl p-1">
          {(['all', 'server', 'local'] as const).map(s => (
            <button key={s} onClick={() => setFilterSource(s)}
              className={`px-3 py-1 rounded-lg text-[11px] font-medium transition-all ${
                filterSource === s ? 'bg-rb-brand text-rb-surface' : 'text-slate-400 hover:text-slate-200'
              }`}>
              {s === 'all' ? 'All' : s === 'server' ? 'DB' : 'Local'}
            </button>
          ))}
        </div>
        <button
          onClick={() => setSortOrder(o => o === 'newest' ? 'oldest' : 'newest')}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 border border-slate-800/50 text-xs font-medium text-slate-400 hover:text-slate-200 transition-all"
        >
          {sortOrder === 'newest' ? <SortDesc className="w-3.5 h-3.5" /> : <SortAsc className="w-3.5 h-3.5" />}
          {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
        </button>
      </div>

      {/* ── Hint ── */}
      <p className="text-[10px] text-slate-600 flex items-center gap-1.5">
        <Info className="w-3 h-3" />
        Click any report card to expand and view full details, sample data, and actions
      </p>

      {/* ── Content ── */}
      {isLoading ? (
        <div className="rb-card p-10 text-center">
          <div className="w-7 h-7 border-2 border-slate-600 border-t-[#cda42e] rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-400">Loading reports…</p>
        </div>
      ) : activeTab === 'sample' ? (
        <>
          {filteredSampleReports.length === 0 ? (
            <div className="rb-card p-12 text-center">
              <FileText className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-400 mb-1">
                {search || filterSource !== 'all' ? 'No matching reports' : 'No sample trend reports yet'}
              </p>
              <p className="text-xs text-slate-600 mt-1 max-w-sm mx-auto">
                Go to Samples, select a customer with 3+ samples on a site, then click Generate Report.
              </p>
              <button onClick={() => navigate('/samples')}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all">
                <TrendingUp className="w-3.5 h-3.5" /> Generate Sample Report
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSampleReports.map(r => <div key={r.id}>{renderSampleCard(r)}</div>)}
              <p className="text-center text-[10px] text-slate-600 pt-1">
                Showing {filteredSampleReports.length} of {sampleReports.length} report{sampleReports.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </>
      ) : activeTab === 'daf' ? (
        <>
          {filteredDAFReports.length === 0 ? (
            <div className="rb-card p-12 text-center">
              <Droplets className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-400 mb-1">
                {search || filterSource !== 'all' ? 'No matching DAF reports' : 'No DAF effluent reports yet'}
              </p>
              <p className="text-xs text-slate-600 mt-1 max-w-sm mx-auto">
                Go to the DAF Calculator, configure your system, save, then click "Regulatory Report" to generate and store a report here.
              </p>
              <button onClick={() => navigate('/daf-spec')}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-all">
                <Droplets className="w-3.5 h-3.5" /> Open DAF Calculator
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDAFReports.map(r => <div key={r.id}>{renderDAFCard(r)}</div>)}
              <p className="text-center text-[10px] text-slate-600 pt-1">
                Showing {filteredDAFReports.length} of {dafReports.length} DAF report{dafReports.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </>
      ) : (
        /* ── Treatment Chains Tab ─────────────────────────────────────────── */
        <>
          {linkedChains.length === 0 ? (
            <div className="rb-card p-12 text-center">
              <Activity className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-400 mb-1">No treatment chains yet</p>
              <p className="text-xs text-slate-600 mt-1 max-w-sm mx-auto">
                Chains are formed when a customer has both a sample trend report and a DAF report. Save a DAF configuration to create a chain.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {linkedChains.map((chain, idx) => {
                const canDownloadChain = !!(chain.sampleReport && chain.dafReport);
                const dafData = chain.dafReport?.reportData;
                const handleChainPDF = (previewMode: boolean) => {
                  if (!chain.dafReport || !dafData?.params) { toast.error('DAF data missing — cannot build chain report'); return; }
                  const infParams: ChainInfluentParams = {
                    ph: dafData.params.ph, cod: dafData.params.cod, bod: dafData.params.bod,
                    tss: dafData.params.tss, nh3n: dafData.params.nh3n, tp: dafData.params.tp,
                    oilgrease: dafData.params.og, flowrate: dafData.params.flowRate,
                    temperature: dafData.params.temp, conductivity: dafData.params.conductivity,
                    turbidity: dafData.params.turbidity,
                  };
                  const dafP: DAFReportParams = dafData.params;
                  const dafR: import('@/lib/pdfGenerator').DAFReportResult = {
                    tssRemoval:  (dafData.tssRemoval ?? 0) / 100,
                    ogRemoval:   (dafData.ogRemoval  ?? 0) / 100,
                    bodRemoval:  (dafData.bodRemoval ?? 0) / 100,
                    codRemoval:  (dafData.codRemoval ?? 0) / 100,
                    effluentTSS: dafData.effluentTSS ?? 0, effluentOG: dafData.effluentOG ?? 0,
                    effluentBOD: dafData.effluentBOD ?? 0, effluentCOD: dafData.effluentCOD ?? 0,
                    effluentPH:  dafP.ph ?? 7,
                    hlr:         dafData.hlr ?? 0, airToSolids: dafData.airToSolids ?? 0,
                    status:      dafData.status ?? 'acceptable',
                  };
                  const effParams: Record<string, number> = {
                    tss: dafData.effluentTSS ?? 0, bod: dafData.effluentBOD ?? 0,
                    cod: dafData.effluentCOD ?? 0, oilgrease: dafData.effluentOG ?? 0,
                    ph: dafP.ph ?? 7,
                  };
                  const chainRef = buildRegulatoryReportNumber('DAF', chain.standard || 'UK_EA');
                  // Actual measured effluent params from linked sample
                  const actualParams = chain.actualEffluentSample
                    ? (typeof chain.actualEffluentSample.params === 'string'
                        ? JSON.parse(chain.actualEffluentSample.params)
                        : chain.actualEffluentSample.params) || null
                    : null;

                  const chainOpts: ChainReportOptions = {
                    chainReportNumber: chainRef,
                    influentReportNumber: chain.sampleReport?.reportNumber,
                    dafReportNumber: chain.dafReport.reportNumber,
                    actualEffluentReportNumber: chain.actualEffluentSample?.reportNumber,
                    customerName: chain.customerName,
                    siteName: chain.dafReport.siteName,
                    siteAddress: chain.dafReport.siteAddress,
                    permitRef: chain.dafReport.sitePermitRef,
                    regulatoryAuthority: chain.dafReport.siteJurisdiction,
                    standard: chain.standard,
                    createdAt: new Date(),
                    actualEfluent: actualParams,
                  };
                  const result = generateInfluentDafEffluentReportPDF(infParams, dafP, dafR, effParams, chainOpts, previewMode ? 'preview' : 'save');
                  if (previewMode && result) {
                    setPreviewUrl(result);
                    setPreviewTitle(`Chain Report — ${chain.customerName}`);
                    setPreviewDownloader(() => () => generateInfluentDafEffluentReportPDF(infParams, dafP, dafR, effParams, chainOpts, 'save'));
                  } else if (!previewMode) {
                    toast.success(`Chain PDF downloaded for ${chain.customerName}`);
                  }
                };

                return (
                  <div key={idx} className="rounded-xl border border-violet-500/20 bg-rb-surface/60 overflow-hidden hover:border-violet-500/40 transition-all">
                    {/* Header */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <div className="w-9 h-9 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center shrink-0">
                        <Activity className="w-4 h-4 text-violet-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-xs font-bold text-white truncate">{chain.customerName}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${stdBadgeClass(chain.standard)}`}>{chain.standard}</span>
                        </div>
                        {/* Chain visual */}
                        <div className="flex items-center gap-1 text-[10px] mt-1 flex-wrap">
                          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border ${
                            chain.sampleReport
                              ? 'bg-rb-brand/10 text-rb-brand border-rb-brand/20'
                              : 'bg-slate-800 text-slate-600 border-slate-700'
                          }`}>
                            <FileText className="w-2.5 h-2.5" />
                            {chain.sampleReport ? `INF ${chain.sampleReport.reportNumber.split('/').pop()}` : 'No Influent'}
                          </span>
                          <ChevronRight className="w-3 h-3 text-slate-600 shrink-0" />
                          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border ${
                            chain.dafReport
                              ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                              : 'bg-slate-800 text-slate-600 border-slate-700'
                          }`}>
                            <Droplets className="w-2.5 h-2.5" />
                            {chain.dafReport ? `DAF ${chain.dafReport.reportNumber.split('/').pop()}` : 'No DAF'}
                          </span>
                          {dafData && (
                            <>
                              <ChevronRight className="w-3 h-3 text-slate-600 shrink-0" />
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                <Beaker className="w-2.5 h-2.5" />
                                EFF (modelled)
                              </span>
                            </>
                          )}
                          {chain.actualEffluentSample && (
                            <>
                              <ChevronRight className="w-3 h-3 text-slate-600 shrink-0" />
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border bg-violet-500/10 text-violet-400 border-violet-500/20">
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                EFF (actual)
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {canDownloadChain && (
                          <>
                            <button
                              onClick={() => handleChainPDF(true)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-slate-800/80 text-slate-300 border border-slate-800/60 hover:bg-slate-700 transition-all"
                            >
                              <Eye className="w-3 h-3" /> Preview
                            </button>
                            <button
                              onClick={() => handleChainPDF(false)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold bg-violet-500/15 text-violet-300 border border-violet-500/25 hover:bg-violet-500/25 transition-all"
                            >
                              <Download className="w-3 h-3" /> Chain PDF
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* DAF KPIs strip */}
                    {dafData && (
                      <div className="flex items-center gap-4 px-4 pb-3 pt-0">
                        {[
                          { label: 'TSS', v: dafData.tssRemoval },
                          { label: 'O&G', v: dafData.ogRemoval },
                          { label: 'BOD', v: dafData.bodRemoval },
                          { label: 'COD', v: dafData.codRemoval },
                        ].filter(m => m.v != null).map(m => (
                          <span key={m.label} className="text-[10px] text-slate-500">
                            {m.label} <span className="text-emerald-400 font-bold">{m.v}%↓</span>
                          </span>
                        ))}
                        {dafData.status && (
                          <DAFStatusBadge status={dafData.status} />
                        )}
                      </div>
                    )}
                    {/* Predicted vs Actual delta table */}
                    {chain.actualEffluentSample && dafData && (() => {
                      const actualP = chain.actualEffluentSample
                        ? (typeof chain.actualEffluentSample.params === 'string'
                            ? (() => { try { return JSON.parse(chain.actualEffluentSample.params); } catch { return {}; } })()
                            : chain.actualEffluentSample.params) || {}
                        : {};
                      const predicted: Record<string, number> = {
                        tss: dafData.effluentTSS ?? 0, bod: dafData.effluentBOD ?? 0,
                        cod: dafData.effluentCOD ?? 0, oilgrease: dafData.effluentOG ?? 0,
                      };
                      const DLABELS: Record<string, string> = {
                        tss: 'TSS', bod: 'BOD', cod: 'COD', oilgrease: 'O&G',
                        ph: 'pH', nh3n: 'NH₃-N',
                      };
                      const rows = Object.keys(predicted).filter(k => predicted[k] != null && actualP[k] != null);
                      if (rows.length === 0) return null;
                      return (
                        <div className="px-4 pb-3 border-t border-slate-800/50 pt-3">
                          <p className="text-[9px] font-bold text-violet-400 uppercase tracking-wider mb-2">
                            Predicted vs Actual Discharge Monitoring
                          </p>
                          <div className="overflow-x-auto rounded-lg border border-slate-800/40">
                            <table className="w-full text-[10px]">
                              <thead>
                                <tr className="bg-slate-800/60 border-b border-slate-800/40">
                                  <th className="text-left px-2.5 py-1.5 text-slate-400 font-semibold">Param</th>
                                  <th className="text-right px-2.5 py-1.5 text-slate-400 font-semibold">Predicted</th>
                                  <th className="text-right px-2.5 py-1.5 text-slate-400 font-semibold">Actual</th>
                                  <th className="text-right px-2.5 py-1.5 text-slate-400 font-semibold">Delta</th>
                                  <th className="text-center px-2.5 py-1.5 text-slate-400 font-semibold">±20%?</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map(k => {
                                  const pred = predicted[k];
                                  const act = Number(actualP[k]);
                                  const deltaPct = pred !== 0 ? ((act - pred) / pred) * 100 : 0;
                                  const within = Math.abs(deltaPct) <= 20;
                                  return (
                                    <tr key={k} className="border-b border-slate-800/40">
                                      <td className="px-2.5 py-1.5 font-semibold text-slate-300">{DLABELS[k] || k}</td>
                                      <td className="px-2.5 py-1.5 text-right text-slate-400 font-mono">{pred.toFixed(1)}</td>
                                      <td className="px-2.5 py-1.5 text-right text-white font-mono font-semibold">{act.toFixed(1)}</td>
                                      <td className={`px-2.5 py-1.5 text-right font-mono font-bold ${
                                        deltaPct > 0 ? 'text-amber-400' : deltaPct < 0 ? 'text-emerald-400' : 'text-slate-500'
                                      }`}>
                                        {deltaPct > 0 ? '+' : ''}{deltaPct.toFixed(1)}%
                                      </td>
                                      <td className="px-2.5 py-1.5 text-center">
                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
                                          within
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                                        }`}>
                                          {within ? '✓ YES' : '✗ NO'}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
